import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
  signal,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors, type HttpInterceptorFn } from '@angular/common/http';
import { provideUploadHttpInterceptors } from '@lite-workspace/uploader-core';
import { bearerInterceptor, errorInterceptor, initAuthMode, purgeLegacyClientTokens } from '@lite-workspace/shared/session';
import { appRoutes } from './app.routes';
import { ThemeService } from './core/theme.service';
import { SocketService } from './core/socket.service';
import { provideSpartanHlm } from './ui/utils/provide-spartan-hlm';
import { EDITOR_NOTIFIER, EDITOR_VIEWER } from '@lite-workspace/shared/design-editor/ports';
import { readAuthUserId } from './core/auth-identity';
import { PROJECT_JOURNEY_SOCKET } from '@lite-workspace/admin-project-journey/domain';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { NOTIFICATIONS_HOST_PRODUCT } from '@lite-workspace/shared/blocks/notifications';
import { ADMIN_FONTS_DATA, HttpAdminFontsData } from '@lite-workspace/admin-fonts-domain';
import { SentryErrorHandler } from '@lite-workspace/shared/services';

const APP_HTTP_INTERCEPTORS: HttpInterceptorFn[] = [bearerInterceptor, errorInterceptor];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withComponentInputBinding(), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    provideHttpClient(withFetch(), withInterceptors(APP_HTTP_INTERCEPTORS)),
    provideUploadHttpInterceptors(APP_HTTP_INTERCEPTORS),
    provideSpartanHlm(),
    { provide: ErrorHandler, useExisting: SentryErrorHandler },
    { provide: EDITOR_NOTIFIER, useExisting: ToastService },
    // Зритель редактора пака — пользователь сессии; читается на старте приложения вместе с токеном.
    // Консоль — рабочее место модерации: фоны чужого пака видны со всеми вердиктами, иначе одобрять нечего.
    { provide: EDITOR_VIEWER, useValue: { userId: signal(readAuthUserId()), seesAllVerdicts: signal(true) } },
    // The console is not one of the inbox products, so it deliberately owns no items: every tagged
    // item's relative link leaves for its own cabinet instead of resolving against this origin.
    { provide: NOTIFICATIONS_HOST_PRODUCT, useValue: 'admin' },
    { provide: PROJECT_JOURNEY_SOCKET, useExisting: SocketService },
    { provide: ADMIN_FONTS_DATA, useClass: HttpAdminFontsData },
    provideAppInitializer(() => void inject(ThemeService)),
    provideAppInitializer(() => initAuthMode()),
    provideAppInitializer(() => purgeLegacyClientTokens()),
  ],
};
